import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { signInWithSlack } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

export const LoginForm: React.FC = () => {
  const [slackLoading, setSlackLoading] = useState(false)
  const [error, setError] = useState('')
  
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  // Check for error in URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlError = searchParams.get('error')
    if (urlError) {
      setError(decodeURIComponent(urlError))
      // Clear the error from URL
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.delete('error')
      const newUrl = `${location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`
      navigate(newUrl, { replace: true })
    }
  }, [location, navigate])

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }, [user, navigate, location])

  const handleSlackLogin = async () => {
    setSlackLoading(true)
    setError('')

    try {
      // Always use standard Slack OAuth flow without plan selection
      // Users will get 7-day trial automatically
      const redirectTo = (location.state as any)?.from?.pathname || '/dashboard'
      
      await signInWithSlack(`${window.location.origin}${redirectTo}`)
      // Note: The redirect happens automatically with OAuth
    } catch (err: any) {
      console.error('Slack login error:', err)
      setError(err.message || 'Failed to sign in with Slack')
      setSlackLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-indigo-100">
            <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.965 8.965 0 01-4.126-.98L3 20l1.98-5.874A8.965 8.965 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Welcome to SyncSimple
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Login with Slack now to get things started
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          {/* Slack Login Button */}
          <button
            onClick={handleSlackLogin}
            disabled={slackLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#4A154B] hover:bg-[#611f69] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4A154B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {slackLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Connecting to Slack...
              </div>
            ) : (
              <div className="flex items-center">
                <img src="/Slack_icon_2019.svg" alt="Slack logo" className="w-5 h-5 mr-2" />
                Sign in with Slack
              </div>
            )}
          </button>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">Why Slack?</span>
              </div>
            </div>
            
            <div className="mt-4 text-sm text-gray-600 text-center">
              <p>SyncSimple integrates directly with your Slack workspace to:</p>
              <ul className="mt-2 text-left space-y-1">
                <li>• Collect daily standups through Slack commands</li>
                <li>• Send automated standup reminders</li>
                <li>• Share team insights in your channels</li>
                <li>• Keep everything synchronized with your workflow</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 